<template>
    <div class="currency-toggle">
        <el-tooltip placement="top">
            <template #content>
                <div class="currency-tooltip">
                    <strong>Currency Display</strong><br>
                    <span class="mode-desc">SOL: Values in SOL</span><br>
                    <span class="mode-desc">USD: Convert to USD using historical prices</span>
                    <div v-if="currentPrice" class="price-info mt-1">
                        Current: 1 SOL = ${{ currentPrice.toFixed(2) }}
                    </div>
                </div>
            </template>
            <el-segmented
                v-model="selectedMode"
                :options="options"
                size="small"
                @change="handleChange"
            />
        </el-tooltip>
    </div>
</template>

<script>
import {
    CURRENCY_MODES,
    CURRENCY_LABELS,
    fetchPrices
} from '../composables/useCurrencyToggle';

export default {
    name: 'CurrencyToggle',

    props: {
        modelValue: {
            type: String,
            default: CURRENCY_MODES.SOL
        },
        dateFrom: {
            type: [Date, String],
            default: null
        },
        dateTo: {
            type: [Date, String],
            default: null
        }
    },

    emits: ['update:modelValue', 'change', 'prices-loaded'],

    data() {
        return {
            selectedMode: this.modelValue,
            currentPrice: null,
            loading: false
        };
    },

    computed: {
        options() {
            return [
                { value: CURRENCY_MODES.SOL, label: CURRENCY_LABELS[CURRENCY_MODES.SOL] },
                { value: CURRENCY_MODES.USD, label: CURRENCY_LABELS[CURRENCY_MODES.USD] }
            ];
        }
    },

    watch: {
        modelValue(newVal) {
            this.selectedMode = newVal;
        },
        dateFrom() {
            this.loadPrices();
        },
        dateTo() {
            this.loadPrices();
        }
    },

    mounted() {
        this.loadPrices();
    },

    methods: {
        handleChange(value) {
            this.$emit('update:modelValue', value);
            this.$emit('change', value);
        },

        async loadPrices() {
            if (this.loading) return;
            this.loading = true;

            try {
                const from = this.dateFrom || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
                const to = this.dateTo || new Date();

                const { current, prices } = await fetchPrices(from, to);
                this.currentPrice = current;
                this.$emit('prices-loaded', { current, prices });
            } catch (error) {
                console.error('Failed to load prices:', error);
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
.currency-toggle {
    display: inline-flex;
    align-items: center;
}

.currency-tooltip {
    font-size: 0.8rem;
    line-height: 1.5;
}

.mode-desc {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.75rem;
}

.price-info {
    color: #10f7cf;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
}

:deep(.el-segmented) {
    --el-segmented-bg-color: rgba(34, 197, 94, 0.1);
    --el-segmented-item-selected-bg-color: rgba(34, 197, 94, 0.6);
    --el-segmented-item-selected-color: #fff;
    border: 1px solid rgba(34, 197, 94, 0.4);
    border-radius: 6px;
    background: rgba(34, 197, 94, 0.1);
}

:deep(.el-segmented__item) {
    color: #d1d5db;
    font-size: 0.8rem;
    font-weight: 500;
    padding: 6px 12px;
    transition: all 0.2s ease;
}

:deep(.el-segmented__item:hover) {
    color: #fff;
}

:deep(.el-segmented__item.is-selected) {
    color: #fff;
    font-weight: 600;
}

:deep(.el-segmented__item-selected) {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.8), rgba(16, 247, 207, 0.6)) !important;
    border-radius: 4px;
}
</style>
